import { errorLogsAPI } from "@shared/api/errorLogsService";

/**
 * Servicio para capturar y registrar errores del frontend
 * Captura errores de React, validaciones, renderización y errores globales
 */

class FrontendErrorLogger {
  constructor() {
    this.isInitialized = false;
    this.errorQueue = [];
    this.isProcessing = false;
    this.maxQueueSize = 50;
    this.userId = null;
    this._inConsoleErrorHook = false;
  }

  /**
   * Inicializa el servicio de logging de errores
   */
  initialize() {
    if (this.isInitialized) {
      console.warn("FrontendErrorLogger ya está inicializado");
      return;
    }

    // Obtener el ID del usuario si está autenticado
    this.updateUserId();

    // Configurar manejadores globales de errores
    this.setupGlobalErrorHandlers();

    this.isInitialized = true;
    console.log("✅ Sistema de captura de errores frontend inicializado");
  }

  /**
   * Actualiza el ID del usuario desde el localStorage
   */
  updateUserId() {
    try {
      const userData = localStorage.getItem("usuario");
      if (userData) {
        const user = JSON.parse(userData);
        this.userId = user.id || user.usuario_id || null;
      }
    } catch (error) {
      console.error("Error al obtener ID de usuario:", error);
      this.userId = null;
    }
  }

  /**
   * Configura los manejadores globales de errores
   */
  setupGlobalErrorHandlers() {
    // Capturar errores de JavaScript no manejados
    window.addEventListener("error", (event) => {
      this.handleError({
        error: event.error,
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        type: "JavaScript Error",
      });
    });

    // Capturar promesas rechazadas no manejadas
    window.addEventListener("unhandledrejection", (event) => {
      this.handleError({
        error: event.reason,
        message: event.reason?.message || "Unhandled Promise Rejection",
        type: "Promise Rejection",
      });
    });

    // Capturar errores de recursos (imágenes, scripts, etc.)
    window.addEventListener(
      "error",
      (event) => {
        if (event.target !== window) {
          this.handleError({
            error: new Error(
              `Failed to load resource: ${
                event.target.src || event.target.href
              }`,
            ),
            message: `Resource loading failed`,
            type: "Resource Error",
            url: event.target.src || event.target.href,
          });
        }
      },
      true,
    );

    // Interceptar console.error para capturar errores dentro de bloques catch
    // que no llegan a los handlers globales por estar "manejados"
    const originalConsoleError = console.error.bind(console);
    console.error = (...args) => {
      originalConsoleError(...args);
      // Guardia de re-entrada: evita bucle infinito si el propio logger llama console.error
      if (this._inConsoleErrorHook) return;
      const error = args.find((arg) => arg instanceof Error);
      if (error) {
        this._inConsoleErrorHook = true;
        try {
          this.handleError({
            error,
            message: error.message,
            type: "Caught Error",
          });
        } finally {
          this._inConsoleErrorHook = false;
        }
      }
    };
  }

  /**
   * Maneja un error y lo registra
   * @param {Object} errorInfo - Información del error
   */
  handleError(errorInfo) {
    try {
      const {
        error,
        message,
        filename,
        lineno,
        colno,
        type,
        componentStack,
        url,
        statusCode,
      } = errorInfo;

      // Preparar datos del error
      const errorData = {
        method: "FRONTEND",
        url: url || window.location.href,
        status_code: statusCode || 0, // Usar el status HTTP real si está disponible
        error_message: message || error?.message || "Unknown error",
        error_stack: this.formatStackTrace(error, componentStack),
        error_type: type || "Frontend Error",
        user_id: this.userId,
        execution_time: null,
        ip_address: "frontend", // El backend puede obtener la IP real
        user_agent: navigator.userAgent,
        request_body: this.captureErrorContext(errorInfo),
      };

      // Agregar información adicional del archivo y línea si está disponible
      if (filename) {
        errorData.error_message += ` (${filename}:${lineno}:${colno})`;
      }

      // Agregar a la cola y procesar
      this.queueError(errorData);
    } catch (err) {
      console.error("Error al procesar error del frontend:", err);
    }
  }

  /**
   * Formatea el stack trace del error
   * @param {Error} error - Objeto de error
   * @param {string} componentStack - Stack de componentes de React (si aplica)
   * @returns {string} Stack trace formateado
   */
  formatStackTrace(error, componentStack) {
    let stackTrace = "";

    if (error?.stack) {
      stackTrace = error.stack;
    } else if (error?.toString) {
      stackTrace = error.toString();
    }

    // Si hay un component stack de React, agregarlo
    if (componentStack) {
      stackTrace += "\n\n--- React Component Stack ---\n" + componentStack;
    }

    return stackTrace || "No stack trace available";
  }

  /**
   * Captura contexto adicional del error
   * @param {Object} errorInfo - Información del error
   * @returns {Object} Contexto del error
   */
  captureErrorContext(errorInfo) {
    return {
      pathname: window.location.pathname,
      search: window.location.search,
      timestamp: new Date().toISOString(),
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      screen: {
        width: window.screen.width,
        height: window.screen.height,
      },
      memory: performance.memory?.usedJSHeapSize || "not available",
      connection: navigator.connection?.effectiveType || "unknown",
      online: navigator.onLine,
      cookiesEnabled: navigator.cookieEnabled,
      language: navigator.language,
      platform: navigator.platform,
      errorInfo: {
        filename: errorInfo.filename,
        lineno: errorInfo.lineno,
        colno: errorInfo.colno,
      },
    };
  }

  /**
   * Agrega un error a la cola de procesamiento
   * @param {Object} errorData - Datos del error
   */
  queueError(errorData) {
    // Evitar agregar errores duplicados recientes
    const isDuplicate = this.errorQueue.some(
      (queuedError) =>
        queuedError.error_message === errorData.error_message &&
        Date.now() - queuedError.queuedAt < 5000, // 5 segundos
    );

    if (isDuplicate) {
      console.log("Error duplicado ignorado:", errorData.error_message);
      return;
    }

    // Agregar timestamp de cola
    errorData.queuedAt = Date.now();

    // Agregar a la cola
    this.errorQueue.push(errorData);

    // Limitar tamaño de la cola
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }

    // Procesar la cola
    this.processQueue();
  }

  /**
   * Procesa la cola de errores enviándolos al backend
   */
  async processQueue() {
    if (this.isProcessing || this.errorQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    while (this.errorQueue.length > 0) {
      const errorData = this.errorQueue.shift();

      try {
        // Actualizar ID de usuario por si cambió
        this.updateUserId();
        if (this.userId) {
          errorData.user_id = this.userId;
        }

        // Enviar al backend — usa el endpoint sin auth estricto
        await errorLogsAPI.createFrontendErrorLog(errorData);

        console.log("✅ Error registrado en el backend:", errorData.error_type);
      } catch (error) {
        console.error("❌ Error al enviar error al backend:", error);

        // No reintentar si es 401 (no autenticado), 403 (no autorizado) o 429 (rate limit) - evita bucles infinitos
        const status = error?.response?.status;
        if (status === 401 || status === 403 || status === 429) {
          console.warn(
            `Error de logging descartado (HTTP ${status}): no se reintentará`,
          );
        } else if (!errorData.retryCount) {
          // Si falla por otra razón, volver a agregar a la cola (máximo 3 intentos)
          errorData.retryCount = 1;
          this.errorQueue.push(errorData);
        } else if (errorData.retryCount < 3) {
          errorData.retryCount++;
          this.errorQueue.push(errorData);
        } else {
          console.error("Error descartado después de 3 intentos:", errorData);
        }

        // Esperar un poco antes de continuar
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
    }

    this.isProcessing = false;
  }

  /**
   * Registra un error de validación
   * @param {string} field - Campo que falló la validación
   * @param {string} message - Mensaje de error
   * @param {Object} context - Contexto adicional
   */
  logValidationError(field, message, context = {}) {
    this.handleError({
      error: new Error(`Validation Error: ${field} - ${message}`),
      message: `Validation failed for field "${field}": ${message}`,
      type: "Validation Error",
      componentStack: context.componentStack,
    });
  }

  /**
   * Registra un error de red/API
   * @param {Object} error - Error de axios o fetch
   * @param {Object} config - Configuración de la petición
   */
  logApiError(error, config = {}) {
    const message =
      error.response?.data?.message || error.message || "API request failed";
    const statusCode = error.response?.status || 0;

    this.handleError({
      error: error,
      message: `API Error: ${message}`,
      type: "API Error",
      url: config.url || error.config?.url,
      statusCode,
    });
  }

  /**
   * Registra un error personalizado
   * @param {Error} error - Error
   * @param {string} type - Tipo de error
   * @param {Object} context - Contexto adicional
   */
  logCustomError(error, type = "Custom Error", context = {}) {
    this.handleError({
      error,
      message: error.message || "Custom error occurred",
      type,
      ...context,
    });
  }

  /**
   * Limpia la cola de errores
   */
  clearQueue() {
    this.errorQueue = [];
    console.log("Cola de errores limpiada");
  }

  /**
   * Desactiva el sistema de logging
   */
  disable() {
    this.isInitialized = false;
    this.clearQueue();
    console.log("Sistema de captura de errores desactivado");
  }
}

// Crear instancia singleton
const frontendErrorLogger = new FrontendErrorLogger();

// Exportar instancia y clase
export { frontendErrorLogger, FrontendErrorLogger };
export default frontendErrorLogger;
